
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { featuredBooks } from '../../mocks/books';

export default function Home() {
  const [currentBookIndex, setCurrentBookIndex] = useState(0);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const nextBook = () => {
    setCurrentBookIndex((prev) => (prev + 1) % featuredBooks.length);
  };

  const prevBook = () => {
    setCurrentBookIndex((prev) => (prev - 1 + featuredBooks.length) % featuredBooks.length);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('source', 'homepage_popup');

      const response = await fetch('https://readdy.ai/api/form/d3iinjd4ikgvg63iuelg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => {
          setShowEmailPopup(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.7), rgba(30, 58, 138, 0.7)), url('https://public.readdy.ai/ai/img_res/be5554dbc0f6f6eb809b60a26d91fbf4.jpg')`
        }}
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-2">
            <span className="text-yellow-400">Elbert C. Alexander</span>
          </h1>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Inspiring Dreams, Empowering Lives
          </h2>
          <p className="text-2xl md:text-3xl mb-4 font-light">
            Books, workshops, and training for personal and professional growth.
          </p>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto opacity-90">
            Transform your life through faith-based wisdom and practical guidance that leads to meaningful achievement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop">
              <Button size="lg" className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold">
                Buy "The Dream Journal"
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
              >
                Request Speaking Engagement
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Workshops */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Workshops</h2>
            <p className="text-xl text-slate-600">Transformational experiences that inspire and empower</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {[ 
              {
                title: "Dream Again Workshop",
                description: "Rediscover your purpose and reignite the dreams that have been dormant in your heart.",
                icon: "ri-lightbulb-line",
                image: "https://readdy.ai/api/search-image?query=inspiring%20workshop%20setting%20with%20people%20engaged%20in%20dream%20mapping%20exercise%2C%20warm%20lighting%2C%20collaborative%20atmosphere%2C%20deep%20blue%20and%20gold%20accents&width=400&height=250&seq=workshop1&orientation=landscape"
              },
              {
                title: "Vision Mapping Intensive",
                description: "Create a clear roadmap for your future with practical tools and spiritual guidance.",
                icon: "ri-map-line",
                image: "https://readdy.ai/api/search-image?query=vision%20mapping%20session%20with%20participants%20working%20on%20goal%20boards%2C%20organized%20workspace%2C%20planning%20materials%2C%20inspiring%20environment&width=400&height=250&seq=mapping1&orientation=landscape"
              }
            ].map((workshop, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img 
                  src={workshop.image}
                  alt={workshop.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="p-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                    <i className={`${workshop.icon} text-2xl text-blue-800`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">{workshop.title}</h3>
                  <p className="text-slate-600 mb-4">{workshop.description}</p>
                  <Link to="/workshops">
                    <Button variant="outline" size="sm" className="border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white">
                      Book Workshop
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/workshops">
              <Button size="lg" className="bg-blue-800 hover:bg-blue-900 px-8">
                View All Workshops
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Insights */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Latest Insights</h2>
            <p className="text-xl text-slate-600">Reflections, wisdom, and inspiration from the journey</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <article className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://static.readdy.ai/image/b75c0c2a711814bf71b853c42d7775ec/12603d4a9e244d3dd07e79171db1cab4.png"
                alt="The Power of Recording Your Dreams"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full mb-3">
                  Personal Growth
                </span>
                <h3 className="text-xl font-semibold text-slate-800 mb-3">The Power of Recording Your Dreams</h3>
                <p className="text-slate-600 mb-4">
                  Why writing down your dreams and aspirations is the first step toward making them reality...
                </p>
                <Link to="/blog/power-of-recording-dreams">
                  <Button variant="outline" size="sm" className="border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white">
                    Read More
                  </Button>
                </Link>
              </div>
            </article>

            <article className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://readdy.ai/api/search-image?query=vision%20board%20and%20planning%20materials%20on%20desk%2C%20organized%20workspace%2C%20goal-setting%20concept%20inspirational%20atmosphere&width=400&height=250&seq=insight3&orientation=landscape"
                alt="Creating Your Personal Vision Map"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full mb-3">
                  Purpose
                </span>
                <h3 className="text-xl font-semibold text-slate-800 mb-3">Creating Your Personal Vision Map</h3>
                <p className="text-slate-600 mb-4">
                  Step-by-step guide to clarifying your life's direction and creating a roadmap for meaningful achievement...
                </p>
                <Link to="/blog/creating-personal-vision-map">
                  <Button variant="outline" size="sm" className="border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white">
                    Read More
                  </Button>
                </Link>
              </div>
            </article>
          </div>

          <div className="text-center mt-12">
            <Link to="/blog">
              <Button variant="outline" size="lg" className="border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white">
                View All Insights
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Life?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands who have discovered their purpose and unlocked their potential through 
            faith-based wisdom and practical guidance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop">
              <Button size="lg" className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold">
                Buy "The Dream Journal"
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Work: The Dream Journal */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Featured Work</h2>
            <p className="text-xl text-slate-600">Discover the flagship book that's transforming lives</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="flex justify-center">
                <img 
                  src={featuredBooks[0].image}
                  alt="The Dream Journal"
                  className="w-80 h-96 object-contain rounded-lg shadow-lg"
                />
              </div>
              <div>
                <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
                  Flagship Book
                </span>
                <h3 className="text-4xl font-bold text-slate-800 mb-6">The Dream Journal</h3>
                <p className="text-xl text-slate-600 mb-6">
                  "A guided journey to record your dreams, reflect deeply, and move boldly toward purpose."
                </p>
                <p className="text-lg text-slate-600 mb-8">
                  This transformative journal helps you rediscover your God-given purpose, capture divine inspiration, 
                  and create practical action plans for achieving your dreams. With guided prompts, reflection exercises, 
                  and spiritual insights, it&apos;s more than a journal—it's a roadmap to transformation.
                </p>
                <div className="flex items-center gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-800">${featuredBooks[0].price}</div>
                    <div className="text-sm text-slate-600">Paperback</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-800">${featuredBooks[0].digitalPrice}</div>
                    <div className="text-sm text-slate-600">Digital</div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Link to="/shop">
                    <Button className="bg-blue-800 hover:bg-blue-900">Buy Now</Button>
                  </Link>
                  <Link to="/journal-preview">
                    <Button variant="outline" className="border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white">
                      Journal Preview 
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Elbert Preview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-4xl font-bold text-slate-800 mb-6">About Elbert Alexander</h2>
              
              <p className="text-lg text-slate-600 mb-6">
                Elbert is an entrepreneur, technologist, coach, and author known for integrating innovation with personal growth. His technology and business expertise enable individuals to achieve their goals through effective coaching.
              </p>

              <p className="text-lg text-slate-600 mb-6">
                With extensive experience, Elbert guides personal and professional development by addressing diverse needs, fostering resilience, and encouraging clear goal setting for lasting success.
              </p>

              <p className="text-lg text-slate-600 mb-6">
                "The Dream Journal" is Elbert's guide for discovering purpose and setting actionable goals. It helps readers reflect, define their objectives, and follow a structured set of steps toward meaningful growth.
              </p>

              <p className="text-lg text-slate-600 mb-6">
                Elbert's offerings include his journal, interactive workshops, speaking engagements, and online courses—all designed for practical self-improvement. He combines strategic and spiritual insights to support fulfillment and lasting change.
              </p>

              <Link to="/about">
                <Button variant="outline" className="border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white">
                  Read Full Bio
                </Button>
              </Link>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <img 
                src="https://static.readdy.ai/image/b75c0c2a711814bf71b853c42d7775ec/e586cc191582f260616d9651a146ebba.jfif"
                alt="Elbert Alexander"
                className="w-96 h-[480px] object-cover object-center rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Email Popup */}
      {showEmailPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-slate-800">Join My Readers' Circle</h3>
              <button 
                onClick={() => setShowEmailPopup(false)}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="text-slate-600 mb-6">
              Get exclusive insights, early access to new releases, and a free chapter from "The Dream Journal" 
              to start your transformation journey today.
            </p>
            
            {submitStatus === 'success' && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm">Thank you! Check your email for your free chapter.</p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">Something went wrong. Please try again.</p>
              </div>
            )}

            <form onSubmit={handleEmailSubmit} data-readdy-form>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-50"
                required
              />
              <Button 
                type="submit" 
                className="w-full bg-blue-800 hover:bg-blue-900" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Joining...' : 'Get Free Chapter'}
              </Button>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
